import { schluesselVon } from './auswahl';
import type { Dringlichkeit, KatalogAuswahl, MangelVorlage } from './typen';

/**
 * Mehrfachauswahl (Paket L, 30.09.): mehrere Standardmängel ergeben EINEN
 * Mangel, z. B. PSA → Schutzbrille + Schutzhandschuhe.
 *
 * - Überschrift: gemeinsamer Anfang (Teile getrennt durch „ / “) einmal, die
 *   Reste mit „, “ — „PSA / Augenschutz, Handschutz“. Ohne gemeinsamen Anfang
 *   die Überschriften mit „; “.
 * - Beschreibung: je Punkt eine Zeile „• …“.
 * - Vorschrift: doppelte fallen weg, sonst mit „; “.
 * - Dringlichkeit: die strengste (Sofortmaßnahme vor kürzester Frist).
 * - Katalog-Felder und Piktogramm: vom ersten Punkt mit Knoten (die
 *   Häufigkeit zählt bis auf Weiteres nur diesen einen Punkt).
 * - Chip: gemeinsamer Pfad und `anzahl`.
 *
 * Reihenfolge = Reihenfolge der Auswahl. Freitext und Auswahlen ohne Vorlage
 * zählen nicht; gleiche Punkte nur einmal.
 */

const TITEL_TRENNER = ' / ';

/** Nicht leere, getrimmte Texte ohne Doppelte, Reihenfolge bleibt. */
function eindeutig(texte: readonly string[]): string[] {
    const gesehen = new Set<string>();
    const aus: string[] = [];
    for (const roh of texte) {
        const t = typeof roh === 'string' ? roh.trim() : '';
        if (!t || gesehen.has(t)) continue;
        gesehen.add(t);
        aus.push(t);
    }
    return aus;
}

/** Zahl der gemeinsamen führenden Elemente aller Listen. */
function gemeinsamerAnfang(listen: readonly (readonly string[])[]): number {
    if (!listen.length) return 0;
    const kuerzeste = Math.min(...listen.map((l) => l.length));
    let n = 0;
    while (n < kuerzeste && listen.every((l) => l[n] === listen[0][n])) n++;
    return n;
}

export function titelZusammenfuehren(titel: readonly string[]): string {
    const liste = eindeutig(titel);
    if (liste.length <= 1) return liste[0] ?? '';
    const teile = liste.map((t) => t.split(TITEL_TRENNER).map((s) => s.trim()));
    // Jede Überschrift muss nach dem gemeinsamen Anfang noch einen Rest haben.
    const n = Math.min(gemeinsamerAnfang(teile), Math.min(...teile.map((t) => t.length)) - 1);
    if (n < 1) return liste.join('; ');
    const anfang = teile[0].slice(0, n).join(TITEL_TRENNER);
    const reste = eindeutig(teile.map((t) => t.slice(n).join(TITEL_TRENNER)));
    return `${anfang}${TITEL_TRENNER}${reste.join(', ')}`;
}

export function textZusammenfuehren(texte: readonly string[]): string {
    const liste = eindeutig(texte);
    if (liste.length <= 1) return liste[0] ?? '';
    return liste.map((t) => `• ${t}`).join('\n');
}

export function vorschriftZusammenfuehren(vorschriften: readonly string[]): string {
    return eindeutig(vorschriften).join('; ');
}

const gueltigeFrist = (v: MangelVorlage): number | null =>
    v.dringlichkeit === 'frist' && typeof v.fristTage === 'number' && Number.isFinite(v.fristTage) && v.fristTage >= 1
        ? Math.round(v.fristTage)
        : null;

/** Strengste Dringlichkeit: 'sofort' vor der kürzesten Frist; sonst keine. */
export function dringlichkeitZusammenfuehren(vorlagen: readonly MangelVorlage[]): { dringlichkeit?: Dringlichkeit; fristTage?: number } {
    if (vorlagen.some((v) => v.dringlichkeit === 'sofort')) return { dringlichkeit: 'sofort' };
    const fristen = vorlagen.map(gueltigeFrist).filter((f): f is number => f !== null);
    return fristen.length ? { dringlichkeit: 'frist', fristTage: Math.min(...fristen) } : {};
}

/**
 * Identität eines Punkts wie im Korb (schluesselVon: Knoten bzw. Eintrag des
 * Gesamtkatalogs). Ohne stabilen Schlüssel der ganze Inhalt — zwei Einträge
 * mit gleicher Überschrift und Beschreibung, aber anderer Vorschrift oder
 * Dringlichkeit bleiben so zwei Punkte (Codex-Review 30.09.).
 */
const punktSchluessel = (a: KatalogAuswahl): string =>
    schluesselVon(a) ??
    `inhalt:${JSON.stringify([a.vorlage?.titel, a.vorlage?.text, a.vorlage?.vorschrift, a.vorlage?.dringlichkeit, a.vorlage?.fristTage])}`;

/**
 * Mehrere Auswahlen → eine. null, wenn kein Punkt mit Vorlage bleibt; genau
 * ein Punkt → unverändert zurück.
 */
export function auswahlZusammenfuehren(auswahlen: readonly KatalogAuswahl[]): KatalogAuswahl | null {
    const gesehen = new Set<string>();
    const punkte: (KatalogAuswahl & { vorlage: MangelVorlage })[] = [];
    for (const a of auswahlen) {
        if (!a || a.art === 'freitext' || !a.vorlage) continue;
        const s = punktSchluessel(a);
        if (gesehen.has(s)) continue;
        gesehen.add(s);
        punkte.push(a as KatalogAuswahl & { vorlage: MangelVorlage });
    }
    if (!punkte.length) return null;
    if (punkte.length === 1) return punkte[0];

    const vorlagen = punkte.map((p) => p.vorlage);
    const erster = punkte[0];
    // Leitpunkt für Katalog-Felder und Piktogramm: der erste mit Knoten.
    const leit = punkte.find((p) => p.art === 'knoten' && p.katalogKey) ?? erster;
    const pfad = erster.pfad.slice(0, gemeinsamerAnfang(punkte.map((p) => p.pfad)));
    const vorlage: MangelVorlage = {
        titel: titelZusammenfuehren(vorlagen.map((v) => v.titel)),
        text: textZusammenfuehren(vorlagen.map((v) => v.text)),
        vorschrift: vorschriftZusammenfuehren(vorlagen.map((v) => v.vorschrift)),
        ...dringlichkeitZusammenfuehren(vorlagen),
        platzhalter: vorlagen.some((v) => v.platzhalter),
    };
    return {
        art: leit.art === 'knoten' && leit.katalogKey ? 'knoten' : 'eintrag',
        katalogKey: leit.art === 'knoten' ? leit.katalogKey ?? null : null,
        katalogKategorie: leit.katalogKategorie ?? erster.katalogKategorie ?? null,
        vorlage,
        pfad,
        pikto: leit.pikto,
        anzahl: punkte.length,
    };
}
